import { useState, Fragment } from 'react';
import { Layout } from '@/components/layout/Layout';
import {
  ChevronLeft,
  ChevronRight,
  Flame,
  AlertTriangle,
  AlertCircle,
  Copy,
  Check,
  Bug,
  Layers,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MetricCard } from '@/components/common/MetricCard';
import {
  ListaMobile,
  ItemLista,
  Selo,
  IconeQuadrado,
  GradeNumeros,
} from '@/components/common/ListaMobile';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCardsRowSkeleton, DistributionBarSkeleton } from '@/components/common/Skeletons';
import { useErrors } from '@/hooks/useErrors';
import { useRealtimeErrors } from '@/hooks/useRealtimeErrors';
import { ErrorDetailsModal } from './ErrorDetailsModal';
import type { ErrorStatus, ErrorSeverity, AppError } from '@/types/error.types';
import { formatRelativeTime } from '@/utils/formatters';

const SEVERITY_PILL =
  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold';

const getSeverityBadge = (severity: string) => {
  switch (severity) {
    case 'critical':
      return (
        <span className={`${SEVERITY_PILL} bg-error/10 text-error`}>
          <Flame size={14} />
          Crítico
        </span>
      );
    case 'high':
      return (
        <span className={`${SEVERITY_PILL} bg-warning/10 text-warning`}>
          <AlertCircle size={14} />
          Alto
        </span>
      );
    case 'medium':
      return (
        <span className={`${SEVERITY_PILL} bg-muted text-text-secondary`}>
          <AlertTriangle size={14} />
          Médio
        </span>
      );
    default:
      return null;
  }
};

const getStatusBadge = (status: string) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
      status === 'resolved' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
    }`}
  >
    {status === 'resolved' ? 'Resolvido' : 'Não resolvido'}
  </span>
);

// Gravidade na lista do celular: ícone no quadrado e rótulo do sobretítulo.
// Crítico em vermelho, alto/médio em laranja, o resto neutro.
const TOM_CRITICO = { cor: 'text-error', fundo: 'bg-error/15 text-error' };
const TOM_AVISO = { cor: 'text-warning', fundo: 'bg-warning/15 text-warning' };
const TOM_NEUTRO = { cor: 'text-text-secondary', fundo: 'bg-muted text-text-secondary' };

const SEVERIDADE_MOBILE: Record<
  string,
  { rotulo: string; icone: typeof Flame; cor: string; fundo: string }
> = {
  critical: { rotulo: 'Crítico', icone: Flame, ...TOM_CRITICO },
  high: { rotulo: 'Alto', icone: AlertCircle, ...TOM_AVISO },
  medium: { rotulo: 'Médio', icone: AlertTriangle, ...TOM_AVISO },
  low: { rotulo: 'Baixo', icone: AlertTriangle, ...TOM_NEUTRO },
  error: { rotulo: 'Erro', icone: AlertCircle, ...TOM_NEUTRO },
};

const plural = (n: number, um: string, varios: string) =>
  `${n.toLocaleString('pt-BR')} ${n === 1 ? um : varios}`;

// "há 2 h", "ontem", "há 3 dias", "14/10" — cabe no sobretítulo da linha.
function quandoCurto(data: string | null | undefined): string {
  if (!data) return '—';
  const d = new Date(data);
  if (isNaN(d.getTime())) return '—';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `há ${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias === 1) return 'ontem';
  if (dias < 7) return `há ${dias} dias`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function ErrorsAndCrashes() {
  useRealtimeErrors();

  const [statusFilter, setStatusFilter] = useState<'all' | ErrorStatus>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | ErrorSeverity>('all');
  const [selectedError, setSelectedError] = useState<AppError | null>(null);
  const [copiedErrorId, setCopiedErrorId] = useState<string | null>(null);

  const { data, isLoading, error } = useErrors({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    severity: severityFilter !== 'all' ? severityFilter : undefined,
  });

  const errors = (data?.errors || []).sort((a, b) => {
    if (a.status === 'unresolved' && b.status === 'resolved') return -1;
    if (a.status === 'resolved' && b.status === 'unresolved') return 1;
    return new Date(b.lastOccurrence).getTime() - new Date(a.lastOccurrence).getTime();
  });

  const unresolvedCount = errors.filter((e) => e.status === 'unresolved').length;
  const totalOccurrences = errors.reduce((sum, e) => sum + (e.occurrences || 0), 0);
  const crashRateNum =
    errors.length > 0 ? (errors.filter((e) => e.type === 'crash').length / errors.length) * 100 : 0;
  const crashRate = crashRateNum.toFixed(2);

  // Distribuição por severidade (dado real da lista) para a barra segmentada
  const severityDist = [
    { key: 'critical', label: 'Crítico', bar: 'bg-error', dot: 'bg-error' },
    { key: 'high', label: 'Alto', bar: 'bg-warning', dot: 'bg-warning' },
    { key: 'medium', label: 'Médio', bar: 'bg-text-secondary', dot: 'bg-text-secondary' },
  ].map((s) => ({ ...s, count: errors.filter((e) => e.severity === s.key).length }));
  const knownSev = severityDist.reduce((sum, s) => sum + s.count, 0);
  const otherSevCount = Math.max(0, errors.length - knownSev);

  const getSeverityLabel = (severity: string) => {
    const labels: Record<string, string> = {
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    };
    return labels[severity] || severity;
  };

  const handleCopyError = async (error: AppError) => {
    const errorText = `
=== ERRO - ${error.message} ===

ID: ${error.id}
Severidade: ${getSeverityLabel(error.severity)}
Status: ${error.status === 'resolved' ? 'Resolvido' : 'Não Resolvido'}
Tipo: ${error.type || 'N/A'}

Primeira Ocorrência: ${error.firstOccurrence}
Última Ocorrência: ${error.lastOccurrence}
Total de Ocorrências: ${error.occurrences || 0}
Dispositivos Afetados: ${error.affectedDevices || 0}

${error.affectedVersions && error.affectedVersions.length > 0 ? `Versões Afetadas: ${error.affectedVersions.join(', ')}\n` : ''}
${error.stackTrace ? `\nStack Trace:\n${error.stackTrace}` : ''}
${error.notes ? `\nNotas de Resolução:\n${error.notes}` : ''}
    `.trim();

    try {
      await navigator.clipboard.writeText(errorText);
      setCopiedErrorId(error.id);
      setTimeout(() => setCopiedErrorId(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('Não foi possível copiar para o clipboard');
    }
  };

  if (error) {
    return (
      <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Erros & Crashes' }]}>
        <div className="p-3 sm:p-4 lg:p-8">
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <h2 className="mb-2 text-xl font-semibold text-text-title">Erro ao carregar erros</h2>
              <p className="text-sm text-text-secondary">
                Não foi possível carregar a lista de erros. Tente novamente.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Erros & Crashes' }]}>
      <div className="p-3 sm:p-4 lg:p-8">
        {/* Page Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-3xl font-semibold text-text-title max-md:hidden">
              Erros e Crashes
            </h2>
            <p className="text-base text-text-secondary">
              Monitore, agrupe e resolva os erros que afetam seus usuários.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 max-md:grid max-md:w-full max-md:grid-cols-1">
            {/* Status */}
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}
            >
              <SelectTrigger className="min-w-[180px] gap-2 max-md:w-full max-md:min-w-0">
                <span className="text-text-secondary">Status:</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="unresolved">Não resolvido</SelectItem>
                <SelectItem value="resolved">Resolvido</SelectItem>
              </SelectContent>
            </Select>

            {/* Severidade */}
            <Select
              value={severityFilter}
              onValueChange={(v) => setSeverityFilter(v as typeof severityFilter)}
            >
              <SelectTrigger className="min-w-[180px] gap-2 max-md:w-full max-md:min-w-0">
                <span className="text-text-secondary">Severidade:</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="critical">Crítico</SelectItem>
                <SelectItem value="high">Alto</SelectItem>
                <SelectItem value="medium">Médio</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Cards */}
        {isLoading ? (
          <>
            <Skeleton className="mt-4 h-[68px] w-full rounded-2xl lg:hidden" />
            <div className="hidden lg:mt-6 lg:block">
              <MetricCardsRowSkeleton count={4} />
            </div>
          </>
        ) : (
          <GradeNumeros
            className="mt-4 lg:hidden"
            itens={[
              { rotulo: 'não resolvidos', valor: unresolvedCount, cor: 'text-error' },
              { rotulo: 'ocorrências', valor: totalOccurrences.toLocaleString('pt-BR') },
              {
                rotulo: 'taxa de crash',
                valor: `${crashRateNum.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`,
                cor: 'text-warning',
              },
            ]}
          />
        )}
        {!isLoading && (
          <div className="mt-4 hidden grid-cols-2 gap-3 sm:gap-4 lg:mt-6 lg:grid lg:gap-6 xl:grid-cols-4">
            <MetricCard
              title="Erros únicos"
              value={errors.length}
              subtitle="agrupados"
              icon={Bug}
              accent="primary"
            />
            <MetricCard
              title="Total de ocorrências"
              value={totalOccurrences.toLocaleString('pt-BR')}
              subtitle="todos os eventos"
              icon={Layers}
              accent="primary"
            />
            <MetricCard
              title="Não resolvidos"
              value={unresolvedCount}
              subtitle={`de ${errors.length} no total`}
              icon={AlertCircle}
              accent="error"
              valueColor="text-error"
              progress={{ value: unresolvedCount, max: errors.length || 1 }}
            />
            <MetricCard
              title="Taxa de crash"
              value={`${crashRate}%`}
              subtitle="do total de erros"
              icon={Flame}
              accent="warning"
              progress={{ value: crashRateNum, max: 100 }}
            />
          </div>
        )}

        {/* Distribuição por severidade (dado real) */}
        {isLoading && (
          <div className="mt-3 sm:mt-4 lg:mt-6">
            <DistributionBarSkeleton />
          </div>
        )}
        {!isLoading && errors.length > 0 && (
          <div className="mt-3 rounded-xl border border-border bg-card p-4 shadow-card sm:mt-4 lg:mt-6 lg:p-5">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-text-title">Distribuição por severidade</h3>
              <span className="text-xs tabular-nums text-text-secondary">
                {errors.length} erros
              </span>
            </div>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
              {severityDist.map(
                (s) =>
                  s.count > 0 && (
                    <div
                      key={s.key}
                      className={s.bar}
                      style={{ width: `${(s.count / errors.length) * 100}%` }}
                      title={`${s.label}: ${s.count}`}
                    />
                  )
              )}
              {otherSevCount > 0 && (
                <div
                  className="bg-border-strong"
                  style={{ width: `${(otherSevCount / errors.length) * 100}%` }}
                  title={`Outros: ${otherSevCount}`}
                />
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs">
              {severityDist.map((s) => (
                <span key={s.key} className="inline-flex items-center gap-1.5 text-text-secondary">
                  <span className={`size-2 rounded-full ${s.dot}`} />
                  {s.label}
                  <span className="font-semibold tabular-nums text-text-title">{s.count}</span>
                </span>
              ))}
              {otherSevCount > 0 && (
                <span className="inline-flex items-center gap-1.5 text-text-secondary">
                  <span className="size-2 rounded-full bg-border-strong" />
                  Outros
                  <span className="font-semibold tabular-nums text-text-title">
                    {otherSevCount}
                  </span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Lista no celular e no tablet (padrão ItemLista): tocar abre o detalhe */}
        <div className="mt-4 lg:hidden">
          {isLoading ? (
            <ListaMobile titulo="Todos os erros">
              {Array.from({ length: 5 }).map((_, i) => (
                <li key={i} className="flex items-center gap-3 px-4 py-3">
                  <Skeleton className="size-10 shrink-0 rounded-xl" />
                  <div className="flex flex-1 flex-col gap-1.5">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-40" />
                  </div>
                </li>
              ))}
            </ListaMobile>
          ) : errors.length > 0 ? (
            <ListaMobile
              titulo="Todos os erros"
              resumo={
                <span className="text-xs tabular-nums text-text-secondary">
                  {data?.total ?? errors.length} erros
                </span>
              }
            >
              {errors.map((error, index) => {
                const isResolved = error.status === 'resolved';
                const showResolvedDivider =
                  isResolved && index > 0 && errors[index - 1].status === 'unresolved';
                const sev = SEVERIDADE_MOBILE[error.severity] ?? SEVERIDADE_MOBILE.medium;
                const SevIcon = sev.icone;
                const isApi = error.source === 'API' || error.metadata?.source === 'API';
                const versoes = error.affectedVersions?.join(', ');

                return (
                  <Fragment key={error.id}>
                    {showResolvedDivider && (
                      <li className="flex items-center gap-2 bg-success/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-success">
                        <Check size={13} aria-hidden="true" />
                        Erros resolvidos
                      </li>
                    )}
                    <ItemLista
                      className={isResolved ? 'opacity-70' : undefined}
                      inicio={
                        <IconeQuadrado className={sev.fundo}>
                          <SevIcon size={18} aria-hidden="true" />
                        </IconeQuadrado>
                      }
                      sobretitulo={
                        <>
                          <span
                            className={`text-[11px] font-semibold uppercase tracking-wide ${sev.cor}`}
                          >
                            {sev.rotulo}
                          </span>
                          <span className="truncate text-text-hint">
                            · {quandoCurto(error.lastOccurrence)}
                          </span>
                          <span className="ml-auto flex shrink-0 items-center gap-1">
                            {isApi && <Selo tom="aviso">API</Selo>}
                            <Selo tom={isResolved ? 'sucesso' : 'erro'}>
                              {isResolved ? 'Resolvido' : 'Não resolvido'}
                            </Selo>
                          </span>
                        </>
                      }
                      titulo={
                        <span className="font-mono text-[13px] font-medium">{error.message}</span>
                      }
                      apoio={[
                        plural(error.occurrences || 0, 'ocorrência', 'ocorrências'),
                        plural(error.affectedDevices || 0, 'aparelho', 'aparelhos'),
                        versoes,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                      quebrar
                      seta
                      onClick={() => setSelectedError(error)}
                    />
                  </Fragment>
                );
              })}
            </ListaMobile>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-card px-4 py-12 text-center shadow-card">
              <span className="flex size-12 items-center justify-center rounded-full bg-success/10 text-success">
                <Check size={24} />
              </span>
              <div className="flex flex-col gap-1">
                <p className="text-base font-medium text-text-title">Nenhum erro encontrado</p>
                <p className="text-sm text-text-secondary">
                  Não há erros registrados com os filtros selecionados.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Error List (desktop) */}
        <div className="mt-8 hidden rounded-xl border border-border bg-card shadow-sm lg:block">
          <div className="flex items-center justify-between border-b border-border p-4">
            <h3 className="text-lg font-semibold text-text-title">Todos os Erros</h3>
          </div>

          {/* Table */}
          <div className="flex flex-col">
            {isLoading ? (
              <>
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-6 border-b border-border px-6 py-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  <div className="col-span-5">Erro</div>
                  <div className="col-span-2 text-center">Ocorrências</div>
                  <div className="col-span-2 text-center">Dispositivos</div>
                  <div className="col-span-3 text-right">Última Ocorrência</div>
                </div>
                {/* Skeleton Rows (espelham a forma das linhas reais) */}
                <div className="divide-y divide-border">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="grid grid-cols-12 items-center gap-6 px-6 py-4">
                      <div className="col-span-5 flex flex-col gap-2">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-5 w-16 rounded-full" />
                          <Skeleton className="h-5 w-24 rounded-full" />
                        </div>
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-3 w-40" />
                      </div>
                      <div className="col-span-2 flex justify-center">
                        <Skeleton className="h-4 w-12" />
                      </div>
                      <div className="col-span-2 flex justify-center">
                        <Skeleton className="h-4 w-10" />
                      </div>
                      <div className="col-span-3 flex items-center justify-end gap-6">
                        <Skeleton className="h-3 w-20" />
                        <Skeleton className="h-9 w-28 rounded-lg" />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : errors.length > 0 ? (
              <>
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-6 border-b border-border px-6 py-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  <div className="col-span-5">Erro</div>
                  <div className="col-span-2 text-center">Ocorrências</div>
                  <div className="col-span-2 text-center">Dispositivos</div>
                  <div className="col-span-3 text-right">Última Ocorrência</div>
                </div>

                {/* Table Rows */}
                <div className="divide-y divide-border">
                  {errors.map((error, index) => {
                    const isResolved = error.status === 'resolved';
                    const prevError = index > 0 ? errors[index - 1] : null;
                    const showResolvedDivider = isResolved && prevError?.status === 'unresolved';

                    return (
                      <Fragment key={error.id}>
                        {showResolvedDivider && (
                          <div className="bg-success/5 px-6 py-3">
                            <div className="flex items-center gap-3">
                              <div className="h-px flex-1 bg-success/25"></div>
                              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-success">
                                <Check size={14} />
                                Erros Resolvidos
                              </span>
                              <div className="h-px flex-1 bg-success/25"></div>
                            </div>
                          </div>
                        )}
                        <div
                          className={`grid grid-cols-12 items-center gap-6 px-6 py-4 transition-colors ${
                            isResolved
                              ? 'bg-success/[0.04] opacity-70 hover:bg-success/10 hover:opacity-100'
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className="col-span-5 flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              {getSeverityBadge(error.severity)}
                              {getStatusBadge(error.status)}
                              {(error.source === 'API' || error.metadata?.source === 'API') && (
                                <span className="rounded-full bg-warning/15 px-2 py-0.5 text-xs font-semibold text-warning">
                                  API
                                </span>
                              )}
                            </div>
                            <p className="line-clamp-2 text-sm font-medium text-text-title">
                              {error.message}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-text-secondary">
                              {error.stackTrace && (
                                <span className="rounded bg-muted px-1.5 py-0.5 font-mono truncate max-w-[200px]">
                                  {error.stackTrace.split('\n')[0]}
                                </span>
                              )}
                              <span>em</span>
                              {error.affectedVersions?.map((version) => (
                                <span
                                  key={version}
                                  className="rounded bg-muted px-1.5 py-0.5 font-mono"
                                >
                                  {version}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="col-span-2 text-center text-sm tabular-nums text-text-title">
                            {error.occurrences?.toLocaleString('pt-BR') || '0'}
                          </div>
                          <div className="col-span-2 flex items-center justify-center gap-3 text-sm text-text-title">
                            <span className="min-w-[2ch] tabular-nums">
                              {error.affectedDevices || '0'}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyError(error);
                              }}
                              aria-label="Copiar erro"
                              title="Copiar erro"
                              className={`flex size-7 shrink-0 items-center justify-center rounded transition-colors active:scale-95 ${
                                copiedErrorId === error.id
                                  ? 'bg-success/15 text-success'
                                  : 'text-text-secondary hover:bg-muted hover:text-text-title'
                              }`}
                            >
                              {copiedErrorId === error.id ? (
                                <Check size={14} />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>
                          <div className="col-span-3 flex items-center justify-end gap-6">
                            <span className="shrink-0 whitespace-nowrap text-sm text-text-secondary">
                              {error.lastOccurrence
                                ? formatRelativeTime(error.lastOccurrence)
                                : '—'}
                            </span>
                            <button
                              onClick={() => setSelectedError(error)}
                              className="h-9 shrink-0 whitespace-nowrap rounded-lg bg-cta px-4 text-sm font-medium text-white shadow-cta transition-colors hover:bg-cta-hover active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-cta/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card"
                            >
                              Ver detalhes
                            </button>
                          </div>
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
                <span className="flex size-12 items-center justify-center rounded-full bg-success/10 text-success">
                  <Check size={24} />
                </span>
                <div className="flex flex-col gap-1">
                  <p className="text-base font-medium text-text-title">Nenhum erro encontrado</p>
                  <p className="text-sm text-text-secondary">
                    Não há erros registrados com os filtros selecionados.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {!isLoading && errors.length > 0 && (
            <div className="flex items-center justify-between border-t border-border p-4 text-sm text-text-secondary">
              <span>
                Mostrando 1-{errors.length} de {data?.total ?? errors.length} erros
              </span>
              <div className="flex items-center gap-2">
                <button className="flex h-8 w-8 items-center justify-center rounded-lg border border-border-strong hover:bg-muted">
                  <ChevronLeft size={18} />
                </button>
                <button className="flex h-8 w-8 items-center justify-center rounded-lg border border-border-strong hover:bg-muted">
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Details Modal */}
      {selectedError && (
        <ErrorDetailsModal
          isOpen={!!selectedError}
          onClose={() => setSelectedError(null)}
          error={selectedError}
        />
      )}
    </Layout>
  );
}
